import { useEffect, useRef } from "react";
import { motion, useScroll, useReducedMotion } from "framer-motion";
import manifesto from "@/content/manifesto.json";
import "@/pages/manifesto.css";

const groups = [];
for (const block of manifesto.blocks.slice(5)) {
  if (block.style === "Heading1") groups.push({ heading: block, paragraphs: [] });
  else groups[groups.length - 1].paragraphs.push(block);
}
const emphasis = new Set([3, 4, 9, 17, 21, 22, 25, 26, 27, 30, 35, 50]);
const Text = ({ block, as: Tag = "p", className = "" }) => <Tag data-manifesto-block={block.id} className={className}>{block.text}</Tag>;

export default function Manifesto() {
  const article = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: article, offset: ["start start", "end end"] });
  useEffect(() => {
    const previous = document.title;
    document.title = "The Ocean Deserves Moonshots — x1025 Manifesto";
    return () => { document.title = previous; };
  }, []);
  return (
    <article ref={article} className="manifesto-page" data-testid="manifesto-page" data-analytics-section="manifesto" data-source-sha256={manifesto.sourceSha256}>
      <motion.div aria-hidden="true" className="manifesto-progress" style={{ scaleX: scrollYProgress }} />
      <header className="manifesto-cover">
        <div className="manifesto-orbit" style={{ "--ocean-texture": "url('/media/wave.jpg')" }} aria-hidden="true"><div className="manifesto-planet" /><div className="manifesto-orbit-line" /></div>
        <div className="shell manifesto-cover-content">
          <Text block={manifesto.blocks[0]} as="h1" />
          <Text block={manifesto.blocks[1]} className="manifesto-subtitle" />
        </div>
        <div className="manifesto-descent" aria-hidden="true" />
      </header>
      <div className="manifesto-opening shell">
        <Text block={manifesto.blocks[2]} className="manifesto-lead" />
        <div className="manifesto-opening-pair"><Text block={manifesto.blocks[3]} /><Text block={manifesto.blocks[4]} /></div>
      </div>
      {groups.map(({ heading, paragraphs }, i) => (
        <section key={heading.id} className={`manifesto-chapter manifesto-chapter-${i}`} aria-labelledby={`manifesto-heading-${heading.id}`}>
          {i === 1 && <div className="manifesto-storm" aria-hidden="true"><img src="/media/wave.jpg" alt="" loading="lazy" /></div>}
          {i === 4 && <div className="manifesto-shore" aria-hidden="true"><img src="/media/roc.jpg" alt="" loading="lazy" /></div>}
          <div className="shell manifesto-chapter-grid">
            <div className="manifesto-chapter-heading"><h2 id={`manifesto-heading-${heading.id}`} data-manifesto-block={heading.id}>{heading.text}</h2><span aria-hidden="true" className="manifesto-heading-line" /></div>
            <div className="manifesto-prose">
              {paragraphs.map((block) => (
                <motion.p key={block.id} data-manifesto-block={block.id}
                  initial={reduce ? false : { opacity: .15, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: .15 }} transition={{ duration: .65 }}
                  className={`${emphasis.has(block.id) ? "manifesto-statement" : ""} ${block.text === "" ? "manifesto-space" : ""}`}>{block.text}</motion.p>
              ))}
            </div>
          </div>
        </section>
      ))}
      <footer className="manifesto-signature shell">{manifesto.footer.map((text, i) => <p key={i} data-manifesto-footer={i}>{text}</p>)}</footer>
    </article>
  );
}
